export type FriendSummary = {
  friendUid: string;
  friendName: string | null;
  friendEmail: string | null;
  createdAt: string | null;
};

export type IncomingRequest = {
  fromUid: string;
  fromName: string | null;
  fromEmail: string | null;
  status: string;
  createdAt: string | null;
};

export type OutgoingRequest = {
  friendUid: string;
  friendName: string | null;
  friendEmail: string | null;
  status: string;
  createdAt: string | null;
};

export type FriendsSummaryResponse = {
  friends: FriendSummary[];
  incomingRequests: IncomingRequest[];
  outgoingRequests: OutgoingRequest[];
};

export type FriendSearchResult = {
  uid: string;
  displayName: string;
  email: string | null;
};

export type FriendActivityItem = {
  id: string;
  friendUid: string;
  friendName: string;
  createdAt: string;
  title: string;
  subtitle: string;
  points: number;
  reactionCount: number;
  thumbsUpCount: number;
  heartCount: number;
  reactedByMe: boolean;
  myReactionType?: string;
};

import { auth } from "@/firebase/firebase-config";

export type ReactionReceived = {
  eventId: string;
  eventType: string;
  eventTitle: string;
  eventPoints: number;
  eventCreatedAt: string | null;
  reactorUid: string;
  reactorName: string;
  reactionType: string;
  reactionCreatedAt: string | null;
};

class FriendServiceError extends Error {
  status: number;
  code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.name = "FriendServiceError";
  }
}

async function postJson<T>(url: string, userId: string, body: any): Promise<T> {
  const user = auth().currentUser;
  if (!user) {
    throw new Error("Not authenticated");
  }
  const token = await user.getIdToken();
  
  // Fetch CSRF token
  const csrfResponse = await fetch("/api/csrf-token", {
    credentials: "include",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!csrfResponse.ok) {
    throw new Error("Unable to get security token");
  }

  const { csrfToken } = await csrfResponse.json();
  
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "X-CSRF-Token": csrfToken,
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let errorMessage = "Request failed";
    let errorCode: string | undefined;
    
    const contentType = res.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      try {
        const errorData = await res.json();
        errorCode = errorData.error || errorData.code;
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        // If JSON parsing fails, fall back to text
        errorMessage = await res.text() || errorMessage;
      }
    } else {
      errorMessage = await res.text() || errorMessage;
    }
    
    throw new FriendServiceError(res.status, errorMessage, errorCode);
  }

  return res.json();
}

export async function searchFriendByEmail(
  userId: string,
  email: string
): Promise<FriendSearchResult> {
  return postJson<FriendSearchResult>("/api/friends/search", userId, { email });
}

export async function sendFriendRequest(
  userId: string,
  friendUid: string
): Promise<{ success: boolean }> {
  return postJson("/api/friends/request", userId, { friendUid });
}

export async function respondToFriendRequest(
  userId: string,
  fromUid: string,
  action: "accept" | "reject"
): Promise<{ success: boolean }> {
  return postJson("/api/friends/respond", userId, { fromUid, action });
}

export async function removeFriend(
  userId: string,
  friendUid: string
): Promise<{ success: boolean }> {
  return postJson("/api/friends/remove", userId, { friendUid });
}

export async function getFriendsSummary(
  userId: string
): Promise<FriendsSummaryResponse> {
  const user = auth().currentUser;
  if (!user) {
    throw new Error("Not authenticated");
  }
  const token = await user.getIdToken();
  
  const res = await fetch(`/api/friends/summary`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error("Failed to load friends");
  }

  return res.json();
}

export async function getFriendsActivity(
  userId: string,
  limit = 20
): Promise<{ items: FriendActivityItem[] }> {
  const user = auth().currentUser;
  if (!user) {
    throw new Error("Not authenticated");
  }
  const token = await user.getIdToken();
  
  const params = new URLSearchParams({ limit: String(limit) });
  const res = await fetch(`/api/friends/activity?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error("Failed to load activity");
  }

  return res.json();
}

export async function reactToFriendActivity(
  userId: string,
  ownerUid: string,
  eventId: string,
  reactionType: "thumbsUp" | "heart" = "thumbsUp"
): Promise<{
  reactionCount: number;
  thumbsUpCount: number;
  heartCount: number;
  reactedByMe: boolean;
  myReactionType?: string;
}> {
  return postJson("/api/friends/react", userId, { ownerUid, eventId, reactionType });
}

export async function getReactionsReceived(
  userId: string,
  limit = 20
): Promise<{ reactions: ReactionReceived[] }> {
  const user = auth().currentUser;
  if (!user) {
    throw new Error("Not authenticated");
  }
  const token = await user.getIdToken();
  
  const params = new URLSearchParams({ limit: String(limit) });
  const res = await fetch(`/api/friends/reactions-received?${params.toString()}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });

  if (!res.ok) {
    throw new Error("Failed to load reactions");
  }

  return res.json();
}

export async function sendFriendInvite(
  userId: string,
  email: string,
  message?: string
): Promise<{ success: boolean; message: string }> {
  return postJson("/api/friends/invite", userId, { email, message });
}
